// Can the server group copies into pockets (POST /copies/search `group`,
// mtg-api docs/superpowers/specs/2026-10-19-copies-group-search.md)? The
// grouped search ships dark, and an older server answers 422 `unknown
// field(s): group`, so the client never assumes it:
//
//  - `unknown` until a grouped read answers. Screens show Each copy (the
//    per-line search) while it is unknown and ask a grouped page beside it.
//  - `supported` once a grouped read returns `groups`.
//  - `unsupported` once one is refused (isGroupUnsupported): Each copy for
//    the rest of the page session. Never persisted, so the next session
//    asks again (a deploy turns it on without anyone clearing storage).
//
// Per user (the dark gate is the owner's). A module store with a
// subscription, read through useSyncExternalStore.
import { useSyncExternalStore } from 'react'

export const GROUP_SUPPORT = { unknown: 'unknown', supported: 'supported', unsupported: 'unsupported' } as const
export type GroupSupport = typeof GROUP_SUPPORT[keyof typeof GROUP_SUPPORT]

const state = new Map<string, GroupSupport>()
const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

export function getGroupSupport(userId: string | null): GroupSupport {
  return (userId && state.get(userId)) || GROUP_SUPPORT.unknown
}

function set(userId: string, next: GroupSupport): void {
  if (state.get(userId) === next) return
  state.set(userId, next)
  emit()
}

/** A grouped read returned groups. Never overrides an `unsupported` seen later in the same session. */
export function markGroupSupported(userId: string): void {
  if (getGroupSupport(userId) === GROUP_SUPPORT.unsupported) return
  set(userId, GROUP_SUPPORT.supported)
}

/** A grouped read was refused: Each copy for the rest of the session. */
export function markGroupUnsupported(userId: string): void {
  set(userId, GROUP_SUPPORT.unsupported)
}

export function subscribeGroupSupport(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

export function useGroupSupport(userId: string | null): GroupSupport {
  return useSyncExternalStore(subscribeGroupSupport, () => getGroupSupport(userId), () => GROUP_SUPPORT.unknown)
}

/** Tests only. */
export function __resetGroupSupport(): void {
  state.clear()
  emit()
}
